/**
 * Reed.ai Design System — Radio
 *
 * States:   Default | Hover | Selected
 * Sizes:    Desktop (24×24 indicator, 16px label) | Mobile (24×24 indicator, 14px label)
 *
 * Source: Figma "Reed.ai - Global components" — A / Radio Buttons page
 * Node:   2070:2679 (Radio/Desktop)
 *
 * Design details:
 *  Default  — white bg, 1px border #C2C6DB (piccadilly-blue-70), 100px radius (full circle)
 *  Hover    — bg #C8C9CA (neutral-20), same border
 *  Selected — outer circle: border #CF04A9, inner dot: 16×16 filled #CF04A9 circle
 *  Label    — Red Hat Text Regular 16px piccadilly-blue (#081351), gap 8px from indicator
 *
 * Usage: wrap multiple <Radio> in a <RadioGroup> and pass a shared `name` prop
 * to create a mutually exclusive group.
 */

import React, { useId } from 'react';
import { cn } from '../../utils/cn';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RadioProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  /** Label text shown next to the radio indicator */
  label?: string;
  /** Desktop = 16px label, Mobile = 14px label */
  size?: 'desktop' | 'mobile';
  className?: string;
  labelClassName?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Reed.ai Radio button
 *
 * @example
 * // Group of radios (share the same `name` prop)
 * const [val, setVal] = useState('a');
 *
 * <Radio name="interview" value="a" label="Yes" checked={val === 'a'} onChange={() => setVal('a')} />
 * <Radio name="interview" value="b" label="No"  checked={val === 'b'} onChange={() => setVal('b')} />
 */
export function Radio({
  label,
  size = 'desktop',
  className,
  labelClassName,
  id: idProp,
  checked,
  disabled,
  ...rest
}: RadioProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const isSelected = !!checked;

  return (
    <label
      className={cn(
        'inline-flex items-center gap-2 cursor-pointer select-none group',
        disabled && 'opacity-30 cursor-not-allowed',
        className,
      )}
      htmlFor={id}
    >
      {/* Hidden native input */}
      <input
        {...rest}
        checked={checked}
        className="sr-only peer"
        disabled={disabled}
        id={id}
        type="radio"
      />

      {/* Custom radio indicator */}
      <span
        className={cn(
          // Base
          'relative shrink-0 size-6 rounded-full border transition-colors duration-150',
          'flex items-center justify-center',
          // Default
          !isSelected && 'border-piccadilly-blue-70',
          // Hover (unchecked)
          !isSelected && !disabled && 'group-hover:bg-neutral-20',
          // Selected state
          isSelected && 'border-brand-dark-carnaby-pink',
          // Focus ring
          'peer-focus-visible:ring-2 peer-focus-visible:ring-brand-dark-carnaby-pink peer-focus-visible:ring-offset-2',
        )}
        aria-hidden="true"
      >
        {/* Inner dot — only shown when selected */}
        {isSelected && (
          <span className="size-4 rounded-full border border-brand-dark-carnaby-pink bg-brand-dark-carnaby-pink" />
        )}
      </span>

      {/* Label */}
      {label && (
        <span
          className={cn(
            'font-body font-normal text-text-primary antialiased leading-[1.5]',
            size === 'desktop' ? 'text-body-md' : 'text-body-md-m',
            labelClassName,
          )}
        >
          {label}
        </span>
      )}
    </label>
  );
}

// ─── RadioGroup ───────────────────────────────────────────────────────────────

export interface RadioGroupProps {
  /** Shared name for all radios in this group */
  name: string;
  /** Current selected value */
  value?: string;
  /** Called when the selection changes */
  onChange?: (value: string) => void;
  /** Vertical or horizontal layout */
  orientation?: 'vertical' | 'horizontal';
  className?: string;
  children: React.ReactNode;
}

/**
 * Wrapper that handles controlled state for a set of Radio components.
 *
 * @example
 * const [selected, setSelected] = useState('yes');
 * <RadioGroup name="confirm" value={selected} onChange={setSelected}>
 *   <Radio value="yes" label="Yes" />
 *   <Radio value="no"  label="No" />
 * </RadioGroup>
 */
export function RadioGroup({
  name,
  value,
  onChange,
  orientation = 'horizontal',
  className,
  children,
}: RadioGroupProps) {
  return (
    <div
      className={cn(
        'flex',
        orientation === 'vertical' ? 'flex-col gap-3' : 'flex-row gap-6 flex-wrap',
        className,
      )}
      role="radiogroup"
    >
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;
        const radioEl = child as React.ReactElement<RadioProps>;
        return React.cloneElement(radioEl, {
          name,
          checked: value !== undefined ? radioEl.props.value === value : radioEl.props.checked,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            onChange?.(e.target.value);
            radioEl.props.onChange?.(e);
          },
        });
      })}
    </div>
  );
}

export default Radio;
